import { FormattedMessage, defineMessages } from 'react-intl';

import type { SupportedCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { BodyM, HeadlineXS, LabelS } from '@sorare/core/src/atoms/typography';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { Sum } from '@sorare/core/src/hooks/useMonetaryAmount';
import { isFiatCurrency } from '@sorare/core/src/lib/fiat';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import { Row } from '#src/components/offer/Row/index.tsx';
import { MarketFeeStatus } from '#src/hooks/useMarketFeesHelperStatus.ts';

const offerMessages = defineMessages({
  initialOffer: {
    id: 'FeesDetails.total',
    defaultMessage: 'Total',
  },
  marketFees: {
    id: 'FeesDetails.marketFees',
    defaultMessage: 'Market fee',
  },
  partialMarketFees: {
    id: 'FeesDetails.marketFees.partial',
    defaultMessage: 'Market fee (up to)',
  },
  youReceive: {
    id: 'FeesDetails.youReceive',
    defaultMessage: 'You receive',
  },
  partialYouReceive: {
    id: 'FeesDetails.youReceive.partial',
    defaultMessage: 'You receive (at least)',
  },
});

const completedOfferUniqueMessages = defineMessages({
  initialOffer: {
    id: 'FeesDetails.listedPrice',
    defaultMessage: 'Listed price',
  },
  marketFees: {
    id: 'FeesDetails.marketFees',
    defaultMessage: 'Market fee',
  },
  youReceive: {
    id: 'FeesDetails.youReceived',
    defaultMessage: 'You received',
  },
});

const completedOfferMessages = {
  initialOffer: completedOfferUniqueMessages.initialOffer,
  marketFees: completedOfferUniqueMessages.marketFees,
  youReceive: completedOfferUniqueMessages.youReceive,
  partialMarketFees: completedOfferUniqueMessages.marketFees,
  partialYouReceive: completedOfferUniqueMessages.youReceive,
};

type Props = {
  monetaryAmount: MonetaryAmount;
  marketFeeMonetaryAmount: MonetaryAmount;
  referenceCurrency: SupportedCurrency;
  completed?: boolean;
  marketFeeStatus?: MarketFeeStatus.ENABLED | MarketFeeStatus.PARTIALLY_ENABLED;
};

export const FeesTooltipFromProps = ({
  monetaryAmount,
  marketFeeMonetaryAmount,
  referenceCurrency,
  completed,
  marketFeeStatus,
}: Props) => {
  const isFiat = isFiatCurrency(referenceCurrency);
  const total = Sum([monetaryAmount, marketFeeMonetaryAmount.multipiedBy(-1)]);
  const messages = completed ? completedOfferMessages : offerMessages;

  return (
    <>
      <Row
        borderless
        title={
          <BodyM>
            <FormattedMessage {...messages.initialOffer} />
          </BodyM>
        }
      >
        <BodyM>
          <AmountWithConversion
            monetaryAmount={monetaryAmount}
            primaryCurrency={isFiat ? Currency.FIAT : Currency.ETH}
            hideExponent
          />
        </BodyM>
      </Row>
      <Row
        borderless
        title={
          <BodyM>
            <FormattedMessage
              {...(marketFeeStatus === MarketFeeStatus.PARTIALLY_ENABLED
                ? messages.partialMarketFees
                : messages.marketFees)}
            />
          </BodyM>
        }
      >
        <BodyM>
          <AmountWithConversion
            monetaryAmount={{ ...marketFeeMonetaryAmount, referenceCurrency }}
            primaryCurrency={isFiat ? Currency.FIAT : Currency.ETH}
            hideExponent
          />
        </BodyM>
      </Row>
      <Row borderless>
        <LabelS>
          <FormattedMessage
            id="FeesTooltipFromProps.marketFeeNotAppliedToEveryCard"
            defaultMessage="The market fee may not apply to every card involved in the transaction."
          />
        </LabelS>
      </Row>
      <Row
        borderless
        title={
          <HeadlineXS>
            <FormattedMessage
              {...(marketFeeStatus === MarketFeeStatus.PARTIALLY_ENABLED
                ? messages.partialYouReceive
                : messages.youReceive)}
            />
          </HeadlineXS>
        }
      >
        <HeadlineXS>
          <AmountWithConversion
            monetaryAmount={total}
            primaryCurrency={isFiat ? Currency.FIAT : Currency.ETH}
            hideExponent
          />
        </HeadlineXS>
      </Row>
    </>
  );
};
